/**
 * Credit costs for various features.
 * Re-exported from centralized tiers.ts for backward compatibility.
 * 
 * @deprecated Import from '@/config/tiers' instead
 */
import { 
  CREDIT_COSTS as COSTS,
  AI_UPGRADE_COSTS as UPGRADES,
  getAIUpgradeCost as getUpgradeCost,
  TIER_LIMITS as LIMITS,
  AI_QUALITY_DISPLAY,
  AI_QUALITY_BY_TIER,
  SubscriptionTier,
  AIQualityLevel,
} from './tiers';

// Re-export from tiers
export const CREDIT_COSTS = COSTS;
export const AI_UPGRADE_COSTS = UPGRADES;
export const TIER_LIMITS = LIMITS;
export { getUpgradeCost as getAIUpgradeCost };
export type { AIQualityLevel, SubscriptionTier as TierType };

// Legacy type alias
export type FeatureType = keyof typeof COSTS;

/**
 * TIER_AI_QUALITY - Maps tier names to AI quality info
 * This maintains the legacy interface that components expect
 */
export const TIER_AI_QUALITY: Record<SubscriptionTier, { name: string; description: string; badge: string; nameKey: string; descKey: string; badgeKey: string }> = {
  free: AI_QUALITY_DISPLAY[AI_QUALITY_BY_TIER.free],
  pro: AI_QUALITY_DISPLAY[AI_QUALITY_BY_TIER.pro],
  chef_master: AI_QUALITY_DISPLAY[AI_QUALITY_BY_TIER.chef_master],
};

/**
 * @deprecated Use getAIUpgradeCost from tiers.ts
 */
export function getTotalCreditCost(baseCost: number, tier: string, quality: AIQualityLevel): number {
  return baseCost + getUpgradeCost((tier || 'free') as SubscriptionTier, quality);
}

// Legacy feature limits for backward compatibility
export const FEATURE_LIMITS = {
  free: {
    chaosKitchen: { daily: 5, monthly: 200 },
    christmasWizard: { weekly: 24 },
    cursedMode: { daily: 2, monthly: 60 },
    recipeTransform: { daily: 1, monthly: 30 },
    photoAnalysis: { daily: 10, monthly: 50 },
    voiceGuidance: false,
    nutritionCalc: { monthly: 0 },
    recipeImport: { monthly: 0 },
    merchAIContent: { monthly: 0 },
    receiptToMealPlan: { monthly: 1 },
  },
  pro: {
    chaosKitchen: { daily: Infinity, monthly: 300 },
    christmasWizard: { daily: Infinity, monthly: Infinity },
    cursedMode: { daily: Infinity, monthly: 120 },
    recipeTransform: { daily: Infinity, monthly: 60 },
    photoAnalysis: { daily: Infinity, monthly: 100 },
    voiceGuidance: false,
    nutritionCalc: { monthly: 20 },
    recipeImport: { monthly: 10 },
    merchAIContent: { monthly: 20 },
    receiptToMealPlan: { monthly: 10 },
  },
  chef_master: {
    chaosKitchen: { daily: Infinity, monthly: Infinity },
    christmasWizard: { daily: Infinity, monthly: Infinity },
    cursedMode: { daily: Infinity, monthly: Infinity },
    recipeTransform: { daily: Infinity, monthly: Infinity },
    photoAnalysis: { daily: Infinity, monthly: Infinity },
    voiceGuidance: true,
    nutritionCalc: { monthly: Infinity },
    recipeImport: { monthly: Infinity },
    merchAIContent: { monthly: Infinity },
    receiptToMealPlan: { monthly: Infinity },
  },
} as const;

/**
 * Calculate meal plan credits cost
 */
export function calculateMealPlanCredits(
  isPaidUser: boolean,
  days: number,
  mealTypesCount: number
): number {
  const ratePerDay = isPaidUser 
    ? COSTS.aiMealPlanPaidPerDay 
    : COSTS.aiMealPlanFreePerDay;
  return ratePerDay * days * mealTypesCount;
}

export const RECEIPT_OCR_SURPLUS = COSTS.receiptOcrSurplus;

export function calculateReceiptMealPlanCredits(
  isPaidUser: boolean,
  days: number,
  mealTypesCount: number
): number {
  return calculateMealPlanCredits(isPaidUser, days, mealTypesCount) + COSTS.receiptOcrSurplus;
}
